import React, { useEffect } from 'react';
import { useLocalStorage } from 'react-use';
import {
  useAddPushNotificationTokenMutation,
  useUserQuery,
} from '@vette/data-access';
import { date } from '@vette/common-utils';
import { firebase, hooks, message } from '@vette/frontend-utils';
import { useWarning } from '@vette/ui-components';

const ASK_TO_ENABLE_NOTIFICATIONS_EVERY = 1000 * 60 * 60 * 24 * 5; // every 5 days

type Props = { userId: string };

export const PushNotificationMessaging: React.FC<Props> = ({ userId }) => {
  const [addPushNotificationToken] = useAddPushNotificationTokenMutation();

  const warning = useWarning();
  const [maybeLater, setMaybeLater] = useLocalStorage<number>(
    'notifications-denied-ask-again-till'
  );

  const { data } = useUserQuery({ variables: { userId } });
  const { isClientInterviewer, isVetter } = hooks.useRole();

  const showNotificationModal =
    isClientInterviewer || (isVetter && data?.user?.onboardingCompleted);

  useEffect(() => {
    (async () => {
      if (
        showNotificationModal &&
        (!maybeLater || maybeLater < date.utcTimestamp()) &&
        Notification.permission === 'default'
      ) {
        warning.openWarning({
          text: `Don't miss another Vette! Enable notifications to receive Vette requests even when this website is in the background.`,
          okText: 'Enable Notifications',
          cancelText: 'Maybe Later',
          cancelProps: {
            'aria-label': 'Maybe Later',
          },
          onOk: async () => {
            let token: string;
            try {
              token = await firebase.messaging.obtainPushNotificationToken();
            } catch (e) {
              message.error(
                'Failed to enable push notifications. Please adjust your browser settings to enable notifications again.'
              );
              setMaybeLater(
                date.utcFutureTimestamp(ASK_TO_ENABLE_NOTIFICATIONS_EVERY)
              );
              return warning.closeWarning();
            }

            await addPushNotificationToken({
              variables: {
                userId,
                token,
              },
            });
            warning.closeWarning();
            message.success(
              `Your notifications have been successfully enabled! 🎉`
            );
          },
          cancelAfterOk: false,
          onCancel: () => {
            setMaybeLater(
              date.utcFutureTimestamp(ASK_TO_ENABLE_NOTIFICATIONS_EVERY)
            );
          },
        });
      }
    })();
  }, [
    showNotificationModal,
    maybeLater,
    userId,
    addPushNotificationToken,
    setMaybeLater,
    warning,
  ]);

  return null;
};
